import React from 'react';
import styled from 'styled-components';
import { Paragraph, Box, themeUtils as tu } from '@zen-common/components-base';
import { numberUtils, assetUtils } from '@zen/common-utils';
import { NamingUtils, TechnicalData } from '@zen/common-app-parts';

const { toDisplay, toEditDisplay } = numberUtils;

/**
 * Get a Subtitle and Content components to display in a confirm modal
 * @param {Object} params
 * @param {TPriceQuantityTotal} params.original
 * @param {TPriceQuantityTotal} params.rounded
 * @param {('make'|'take')} params.command
 * @param {boolean} params.isBuy
 * @param {string} params.asset - the base asset
 * @param {Array} params.naming - hashtable of assets name
 */
export default function getConfirmExecutionModal({
  original,
  rounded,
  command,
  isBuy,
  asset,
  naming,
  quote,
}) {
  const minPriceLength = Math.min(original.price.length, rounded.price.length);
  const roundedPriceTruncated = rounded.price.substring(0, minPriceLength);
  const originalPriceTruncated = original.price.substring(0, minPriceLength);
  // prevent comparing if the only thing changed is the higher precision
  const makePriceChanged =
    command === 'make' ? originalPriceTruncated !== roundedPriceTruncated : false;
  const changed =
    original.quantity !== rounded.quantity || original.total !== rounded.total || makePriceChanged;

  const Subtitle = () => (
    <Paragraph>
      {changed && (
        <Box as="span" color="warning">
          Amounts were changed due to decimals.
        </Box>
      )}
      <Box as="span" display="block">
        You are about to {command === 'make' ? 'make' : 'take'} a {isBuy ? 'buy' : 'sell'} order of
        <Box as="span" title={asset}>
          {' '}
          {!!naming && naming[asset]
            ? naming[asset].shortName
            : assetUtils.truncateOrNameAsset(asset)}{' '}
        </Box>
        with the following amounts:
      </Box>
    </Paragraph>
  );

  const Content = () =>
    changed ? (
      <ChangedOrdersWrapper>
        <ChangedOrderBox>
          <Box>ORIGINAL ORDER:</Box>
          <TechnicalDataSection
            price={toEditDisplay(original.price)}
            quantity={toEditDisplay(original.quantity)}
            total={toEditDisplay(original.total)}
            base={asset}
            quote={quote}
            naming={naming}
          />
        </ChangedOrderBox>
        <ChangedOrderBox>
          <Box color="warning">CHANGED ORDER:</Box>
          <TechnicalDataSection
            price={toEditDisplay(rounded.price)}
            quantity={toDisplay(rounded.quantity)}
            total={toDisplay(rounded.total)}
            base={asset}
            quote={quote}
            naming={naming}
          />
        </ChangedOrderBox>
      </ChangedOrdersWrapper>
    ) : (
      <TechnicalDataSection
        price={toEditDisplay(original.price)}
        quantity={toEditDisplay(original.quantity)}
        total={toEditDisplay(original.total)}
        base={asset}
        quote={quote}
        naming={naming}
      />
    );

  return { Subtitle, Content };
}

function TechnicalDataSection({ price, quantity, total, naming, base, quote }) {
  return (
    <TechnicalDataContainer>
      <Box>
        <TechnicalData.Label>Price per unit: </TechnicalData.Label>
        <TechnicalData.Value>{price}</TechnicalData.Value>
      </Box>
      <Box>
        <TechnicalData.Label>
          Quantity {base ? ' - ' + NamingUtils.displayName(base, naming) : ''}:{' '}
        </TechnicalData.Label>
        <TechnicalData.Value>{quantity}</TechnicalData.Value>
      </Box>
      <Box>
        <TechnicalData.Label>
          Total {quote ? ' - ' + NamingUtils.displayName(quote, naming) : ''}:{' '}
        </TechnicalData.Label>
        <TechnicalData.Value>{total}</TechnicalData.Value>
      </Box>
    </TechnicalDataContainer>
  );
}

const ChangedOrdersWrapper = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  margin-bottom: calc(-1 * ${tu.space('sm')});
`;

const ChangedOrderBox = styled(Box)`
  line-height: ${tu.lineHeight(5)};
  margin-bottom: ${tu.space('sm')};

  &:first-child {
    margin-right: ${tu.space('xxl')};
  }
  &:last-child {
    margin-bottom: 0;
  }
`;

const TechnicalDataContainer = styled(Box)`
  word-break: break-all;
`;

/**
 * @typedef {{
 *  price: string,
    quantity: string,
    total: string,
 * }} TPriceQuantityTotal
 */
